import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  Lock, 
  Shield, 
  Key, 
  Eye,
  CreditCard,
  Users,
  CheckCircle,
  Star,
  Heart,
  Database,
  Clock,
  Trash2,
  FileText
} from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const PricingPrivacy = () => {
  return (
    <div 
      data-testid="pricing-privacy-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.TERMS_PRIVACY)}
    >
      <div className="fixed inset-0 bg-black/75 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">LEGAL</div>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-[#e67e22] mb-4">
              LICENSING, SECURITY & PRIVACY
            </h1>
            <p className="text-lg text-[#e67e22] max-w-2xl mx-auto">
              Clear information about your subscription, how we protect your data, and our commitment to your privacy.
            </p>
          </div>

          {/* Licensing Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#e67e22]/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-[#e67e22]/20 rounded-lg">
                  <Lock className="w-6 h-6 text-[#e67e22]" />
                </div>
                Licensing & Subscription Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-[#d6d6d6] text-lg">
                CNC-Helpdesk is offered as a subscription-based application.
                <br />
                <strong className="text-white">All paid plans include full access to all features.</strong>
              </p>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {/* Monthly Plan */}
                <div className="bg-black/50 rounded-xl p-6 border border-[#3a3a3a] hover:border-[#e67e22]/50 transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Monthly Plan</h3>
                    <CreditCard className="w-6 h-6 text-[#a3a3a3]" />
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-black text-[#10B981]">$12</span>
                    <span className="text-[#a3a3a3] ml-2">per month</span>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-[#d6d6d6]">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      Full access to all features
                    </li>
                    <li className="flex items-center gap-2 text-[#d6d6d6]">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      Cancel anytime
                    </li>
                  </ul>
                </div>

                {/* Annual Plan */}
                <div className="bg-black/50 rounded-xl p-6 border-2 border-[#e67e22] relative">
                  <Badge className="absolute -top-3 right-4 bg-[#e67e22] text-black font-bold">
                    <Star className="w-3 h-3 mr-1" />
                    Best Value
                  </Badge>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Annual Plan</h3>
                    <CreditCard className="w-6 h-6 text-[#e67e22]" />
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-black text-[#10B981]">$99</span>
                    <span className="text-[#a3a3a3] ml-2">per year</span>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-[#d6d6d6]">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      Full access to all features
                    </li>
                    <li className="flex items-center gap-2 text-[#e67e22] font-medium">
                      <CheckCircle className="w-5 h-5 text-[#e67e22]" />
                      Save over 30% compared to monthly
                    </li>
                  </ul>
                </div>
              </div>

              {/* Individual Use License */}
              <div className="bg-black/50 rounded-xl p-6 border border-[#3a3a3a] mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-blue-400" />
                  <h3 className="text-lg font-bold text-white">Individual Use License</h3>
                </div>
                <p className="text-[#d6d6d6] mb-4">
                  CNC-Helpdesk subscriptions are licensed for individual use.
                </p>
                <p className="text-[#a3a3a3] mb-3">This means:</p>
                <ul className="space-y-2 text-[#d6d6d6]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#e67e22] mt-1">•</span>
                    One subscription is intended for one user
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#e67e22] mt-1">•</span>
                    Sharing login credentials with others is not permitted
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#e67e22] mt-1">•</span>
                    Simultaneous use on multiple devices may be limited
                  </li>
                </ul>
                <p className="text-[#a3a3a3] text-sm mt-4 italic">
                  For workshops or teams requiring multiple users, additional licensing options may be introduced in the future.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Security Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-blue-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-400" />
                </div>
                Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-[#d6d6d6] text-lg">
                We take reasonable and practical steps to protect your account and data.
              </p>

              <p className="text-[#a3a3a3]">CNC-Helpdesk implements security measures such as:</p>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Lock, text: 'Encrypted connections (HTTPS)', color: 'text-green-400' },
                  { icon: Key, text: 'Secure password storage (passwords are never stored in plain text)', color: 'text-blue-400' },
                  { icon: Shield, text: 'Session controls to reduce unauthorised access', color: 'text-purple-400' },
                  { icon: Eye, text: 'Basic monitoring to detect unusual account activity', color: 'text-amber-400' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-black/50 p-4 rounded-lg border border-[#3a3a3a]">
                    <item.icon className={`w-5 h-5 ${item.color} mt-0.5 shrink-0`} />
                    <span className="text-[#d6d6d6]">{item.text}</span>
                  </div>
                ))}
              </div>

              <p className="text-[#a3a3a3] text-sm italic">
                We continuously review security practices as the platform evolves.
              </p>
            </CardContent>
          </Card>

          {/* Data We Store Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-cyan-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <Database className="w-6 h-6 text-cyan-400" />
                </div>
                Data We Store
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-[#d6d6d6] text-lg">
                This section provides technical transparency about exactly what data CNC-Helpdesk stores and why.
              </p>

              {/* Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#3a3a3a]">
                      <th className="py-3 px-4 text-[#a3a3a3] font-medium">Data Type</th>
                      <th className="py-3 px-4 text-[#a3a3a3] font-medium">What We Store</th>
                      <th className="py-3 px-4 text-[#a3a3a3] font-medium">Why</th>
                      <th className="py-3 px-4 text-[#a3a3a3] font-medium">How It's Protected</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#d6d6d6]">
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Email Address</td>
                      <td className="py-3 px-4">Your email used to register</td>
                      <td className="py-3 px-4">Account identification & login</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-cyan-400 border-cyan-500/50">Encrypted at rest</Badge></td>
                    </tr>
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Password</td>
                      <td className="py-3 px-4">Cryptographic hash only</td>
                      <td className="py-3 px-4">Secure authentication</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-green-400 border-green-500/50">Bcrypt hashed</Badge></td>
                    </tr>
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Display Name</td>
                      <td className="py-3 px-4">Name you provide</td>
                      <td className="py-3 px-4">Shown in community & marketplace</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-[#a3a3a3] border-gray-500/50">Plain text</Badge></td>
                    </tr>
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Subscription Status</td>
                      <td className="py-3 px-4">Active/inactive, expiry date</td>
                      <td className="py-3 px-4">Access control</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-cyan-400 border-cyan-500/50">Encrypted at rest</Badge></td>
                    </tr>
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Community Posts</td>
                      <td className="py-3 px-4">Messages you post</td>
                      <td className="py-3 px-4">Feature functionality</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-[#a3a3a3] border-gray-500/50">Plain text</Badge></td>
                    </tr>
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Marketplace Listings</td>
                      <td className="py-3 px-4">Items you list for sale</td>
                      <td className="py-3 px-4">Feature functionality</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-[#a3a3a3] border-gray-500/50">Plain text</Badge></td>
                    </tr>
                    <tr className="border-b border-[#3a3a3a]/50">
                      <td className="py-3 px-4 font-medium text-white">Feedback Submissions</td>
                      <td className="py-3 px-4">Bug reports, suggestions</td>
                      <td className="py-3 px-4">Platform improvement</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-cyan-400 border-cyan-500/50">Encrypted at rest</Badge></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium text-white">Payment Info</td>
                      <td className="py-3 px-4 text-green-400">We do NOT store this</td>
                      <td className="py-3 px-4">—</td>
                      <td className="py-3 px-4"><Badge variant="outline" className="text-green-400 border-green-500/50">Handled by Stripe</Badge></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Data Retention */}
              <div className="bg-black/50 rounded-xl p-6 border border-[#3a3a3a]">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-cyan-400" />
                  <h3 className="text-lg font-bold text-white">Data Retention</h3>
                </div>
                <ul className="space-y-2 text-[#d6d6d6]">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span><strong className="text-white">Account data:</strong> Retained while your account is active</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span><strong className="text-white">Community posts:</strong> Retained unless you delete them or your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span><strong className="text-white">Marketplace listings:</strong> Retained until you remove them or delete your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span><strong className="text-white">Upon account deletion:</strong> All your personal data is permanently removed within 30 days</span>
                  </li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="bg-black/50 rounded-xl p-6 border border-[#3a3a3a]">
                <div className="flex items-center gap-3 mb-4">
                  <Trash2 className="w-6 h-6 text-red-400" />
                  <h3 className="text-lg font-bold text-white">Your Rights</h3>
                </div>
                <p className="text-[#d6d6d6] mb-4">You have the right to:</p>
                <ul className="space-y-2 text-[#d6d6d6]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                    <span><strong className="text-white">Access</strong> — View what data we hold about you</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                    <span><strong className="text-white">Correct</strong> — Update your account information anytime</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                    <span><strong className="text-white">Delete</strong> — Permanently delete your account and all associated data from your Profile page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                    <span><strong className="text-white">Export</strong> — Request a copy of your data by contacting us</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Data Usage Section */}
          <Card className="bg-black/60 backdrop-blur-sm border-emerald-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Eye className="w-6 h-6 text-emerald-400" />
                </div>
                Privacy & Data Usage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-[#d6d6d6] text-lg">
                CNC-Helpdesk is designed to collect only the data required to operate the service.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* What we collect */}
                <div className="bg-black/50 rounded-xl p-6 border border-[#3a3a3a]">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    What we collect
                  </h3>
                  <ul className="space-y-3 text-[#d6d6d6]">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">•</span>
                      Account information (such as username and email address)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">•</span>
                      Subscription status (managed via Stripe)
                    </li>
                  </ul>
                </div>

                {/* What we do NOT do */}
                <div className="bg-black/50 rounded-xl p-6 border border-red-500/30">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    What we do NOT do
                  </h3>
                  <ul className="space-y-3 text-[#d6d6d6]">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      We do not sell your data
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      We do not track you across other websites
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-1">✗</span>
                      We do not analyse or resell CNC project data
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-black/50 rounded-lg p-4 border border-[#3a3a3a]">
                <p className="text-[#a3a3a3]">
                  <CreditCard className="w-4 h-4 inline-block mr-2 text-blue-400" />
                  Payments and billing information are handled securely by <strong className="text-white">Stripe</strong>.
                  <br />
                  CNC-Helpdesk does not store or process credit card details.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Passwords & Database Security */}
          <Card className="bg-black/60 backdrop-blur-sm border-purple-500/30 border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Key className="w-6 h-6 text-purple-400" />
                </div>
                Account Credentials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#d6d6d6] text-lg">
                Passwords are securely stored using industry-standard hashing methods (bcrypt).
              </p>
              <p className="text-[#d6d6d6]">
                CNC-Helpdesk administrators <strong className="text-white">cannot view or retrieve your password</strong>.
              </p>
              <div className="bg-black/50 rounded-lg p-4 border border-purple-500/30">
                <p className="text-[#a3a3a3] flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  <span>You are responsible for keeping your login credentials secure.</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Terms of Service Link */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-black/50 rounded-lg shrink-0">
                  <FileText className="w-6 h-6 text-[#a3a3a3]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">Terms of Service</h3>
                  <p className="text-[#a3a3a3]">
                    By using CNC-Helpdesk, you agree to our Terms of Service which outline acceptable use, limitations, and your responsibilities.
                  </p>
                </div>
                <Link to="/terms-of-service" className="text-[#e67e22] hover:text-[#e67e22]/80 font-medium whitespace-nowrap">
                  View Terms →
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Transparency Statement */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#e67e22]/20 border">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-[#e67e22]/20 rounded-full">
                    <Heart className="w-8 h-8 text-[#e67e22]" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Transparency & Trust</h2>
                <p className="text-[#d6d6d6] text-lg max-w-2xl mx-auto mb-4">
                  CNC-Helpdesk is built for makers, hobbyists, and professionals who value reliable tools and honest guidance.
                </p>
                <p className="text-[#a3a3a3] italic">
                  If our data practices change in the future, this page will be updated accordingly.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer spacer */}
          <div className="h-8"></div>
        </div>
    </div>
  );
};

export default PricingPrivacy;
